import { useState, useMemo, useCallback, useEffect } from 'react'
import { useAuth } from '../App.jsx'
import { useApi } from '../hooks/useApi'
import MapView from './MapView'

const Dashboard = () => {
  const { logout } = useAuth()
  const [_collapsed, setCollapsed] = useState(true)
  const [selectedId, setSelectedId] = useState(null)
  const [tab, setTab] = useState('dashboard')
  const [_error, setError] = useState(null)
  const [chatOpen, setChatOpen] = useState(false)
  const [chatMessages, setChatMessages] = useState([
    { id: 1, text: 'Welcome to AGX9 Support! How can I help you today?', sender: 'bot', time: new Date().toLocaleTimeString() }
  ])
  const [chatInput, setChatInput] = useState('')

  const { request, loading, error: apiError } = useApi()
  const [plantations, setPlantations] = useState([])
  const [_meta, setMeta] = useState({ lastSync: null, source: 'Live' })

  const [ndviMin, setNdviMin] = useState('')
  const [ndviMax, setNdviMax] = useState('')
  const [areaMin, setAreaMin] = useState('')
  const [areaMax, setAreaMax] = useState('')
  const [healthFilters, setHealthFilters] = useState({
    unhealthy: true, stressed: true, moderate: true, healthy: true, optimal: true
  })
  const [cropType, setCropType] = useState('')
  const [dataset, setDataset] = useState('plantations')
  const [search, setSearch] = useState('')
  const staleDays = 7

  useEffect(() => {
    setError(apiError)
  }, [apiError])

  useEffect(() => {
    let cancelled = false
    const load = async () => {
      try {
        const data = await request('/api/plantations')
        if (!cancelled && Array.isArray(data)) {
          setPlantations(data)
          setMeta((m) => ({ ...m, lastSync: new Date().toLocaleTimeString() }))
        }
      } catch {
        // handled by hook
      }
    }
    load()
    return () => {
      cancelled = true
    }
  }, [request])

  const handleSelect = useCallback((id) => {
    setSelectedId(id)
    if (window.innerWidth < 900) setCollapsed(false)
  }, [])

  const handleSendMessage = useCallback(() => {
    if (!chatInput.trim()) return
    
    const userMessage = {
      id: Date.now(),
      text: chatInput,
      sender: 'user',
      time: new Date().toLocaleTimeString()
    }
    
    setChatMessages(prev => [...prev, userMessage])
    setChatInput('')
    
    // Simulate bot response
    setTimeout(() => {
      const botResponse = {
        id: Date.now() + 1,
        text: 'Thank you for your message! Our support team will get back to you shortly.',
        sender: 'bot',
        time: new Date().toLocaleTimeString()
      }
      setChatMessages(prev => [...prev, botResponse])
    }, 1000)
  }, [chatInput])

  const healthDistribution = useMemo(() => {
    if (!plantations.length) return { unhealthy: 0, stressed: 0, moderate: 0, healthy: 0, optimal: 0 }
    return plantations.reduce((acc, p) => {
      const ndvi = Number(p.ndvi) || 0
      if (ndvi < 0.3) acc.unhealthy++
      else if (ndvi < 0.5) acc.stressed++
      else if (ndvi < 0.7) acc.moderate++
      else if (ndvi < 0.85) acc.healthy++
      else acc.optimal++
      return acc
    }, { unhealthy: 0, stressed: 0, moderate: 0, healthy: 0, optimal: 0 })
  }, [plantations])

  const topPerformers = useMemo(() => {
    return [...plantations].sort((a, b) => (Number(b.ndvi) || 0) - (Number(a.ndvi) || 0)).slice(0, 5)
  }, [plantations])

  const cropTypes = useMemo(() => {
    const set = new Set(plantations.map(p => p.crop_type).filter(Boolean))
    return [...set].sort()
  }, [plantations])

  const healthForNdvi = (ndvi) => {
    if (ndvi >= 0.85) return 'optimal'
    if (ndvi >= 0.7) return 'healthy'
    if (ndvi >= 0.5) return 'moderate'
    if (ndvi >= 0.3) return 'stressed'
    return 'unhealthy'
  }

  const healthLabel = (ndvi) => {
    if (ndvi >= 0.85) return 'Optimal'
    if (ndvi >= 0.7) return 'Healthy'
    if (ndvi >= 0.5) return 'Moderate'
    if (ndvi >= 0.3) return 'Stressed'
    return 'Unhealthy'
  }

  const healthColor = (ndvi) => {
    if (ndvi >= 0.85) return '#2a9d8f'
    if (ndvi >= 0.7) return '#8ab17d'
    if (ndvi >= 0.5) return '#e9c46a'
    if (ndvi >= 0.3) return '#f2a261'
    return '#d94e3f'
  }

  const staleThreshold = useMemo(() => {
    const d = new Date()
    d.setDate(d.getDate() - staleDays)
    return d
  }, [])

  const ndviFromHealth = (health) => {
    const h = String(health || '').toLowerCase()
    if (h === 'excellent') return 0.82
    if (h === 'good') return 0.65
    if (h === 'fair') return 0.42
    if (h === 'poor') return 0.18
    if (h === 'critical') return 0.10
    if (h === 'unhealthy') return -0.22
    return 0.0
  }

  const enrichedPlantations = useMemo(() => {
    return plantations.map(p => {
      const ndvi = (p.ndvi != null ? Number(p.ndvi) : ndviFromHealth(p.ndvi_health)) || 0
      const ndviDate = p.ndvi_date ? new Date(p.ndvi_date) : null
      const isStale = ndviDate && ndviDate < staleThreshold
      return { ...p, ndvi, isStale, ndviDate }
    })
  }, [plantations, staleThreshold])

  const filteredPlantations = useMemo(() => {
    return enrichedPlantations.filter(p => {
      if (ndviMin !== '' && p.ndvi < Number(ndviMin)) return false
      if (ndviMax !== '' && p.ndvi > Number(ndviMax)) return false
      const h = healthForNdvi(p.ndvi)
      if (!healthFilters[h]) return false
      if (cropType && p.crop_type !== cropType) return false
      if (areaMin !== '' && (Number(p.area_ha) || 0) < Number(areaMin)) return false
      if (areaMax !== '' && (Number(p.area_ha) || 0) > Number(areaMax)) return false
      if (search) {
        const q = search.toLowerCase()
        const name = String(p.name || '').toLowerCase()
        const id = String(p.id || '')
        if (!name.includes(q) && !id.includes(q)) return false
      }
      return true
    })
  }, [enrichedPlantations, ndviMin, ndviMax, areaMin, areaMax, healthFilters, cropType, search])

  const selectedPlantation = useMemo(() => {
    if (!selectedId) return null
    return enrichedPlantations.find(p => p.id === selectedId) || null
  }, [enrichedPlantations, selectedId])

  const sparklinePath = useCallback((ndvi, nPoints = 8) => {
    const base = Math.max(0.05, ndvi)
    const pts = []
    let v = base
    for (let i = 0; i < nPoints; i++) {
      v = Math.max(0.01, Math.min(1, v + (Math.random() - 0.5) * 0.15))
      pts.push(v)
    }
    const w = 100, h = 30
    const xStep = w / (nPoints - 1)
    const min = Math.min(...pts), max = Math.max(...pts)
    const range = Math.max(max - min, 0.05)
    const coords = pts.map((y, i) => {
      const px = i * xStep
      const py = h - ((y - min) / range) * (h - 2) - 1
      return `${px},${py}`
    })
    return { path: `M${coords.join('L')}`, min, max, latest: pts[pts.length - 1], direction: pts[pts.length - 1] >= pts[0] ? 'up' : 'down' }
  }, [])

  const exportCSV = useCallback(() => {
    const rows = [['ID', 'Name', 'NDVI', 'Health', 'Crop Type', 'Area (ha)', 'Last Scan', 'Notes']]
    filteredPlantations.forEach(p => {
      rows.push([p.id, p.name, p.ndvi.toFixed(3), healthLabel(p.ndvi), p.crop_type || '', p.area_ha || '', p.ndvi_date || '', p.notes || ''])
    })
    const csv = rows.map(r => r.map(c => `"${String(c).replace(/"/g, '""')}"`).join(',')).join('\n')
    const blob = new Blob([csv], { type: 'text/csv' })
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url; a.download = `agx9_export_${new Date().toISOString().slice(0, 10)}.csv`
    a.click()
    URL.revokeObjectURL(url)
  }, [filteredPlantations])

  return (
    <div className="app bi-dashboard">
      <div className="bi-header">
        <div className="bi-brand">
          <div className="bi-logo">
            <span className="bi-logo-text">AGX9</span>
            <span className="bi-logo-badge">BI</span>
          </div>
          <div className="bi-title">
            <h1>Geospatial Analytics Dashboard</h1>
            <p>Real-time NDVI monitoring and plantation performance tracking</p>
          </div>
        </div>
        <div className="bi-controls">
          <div className="bi-search-wrap">
            <input className="bi-search-input" type="text" placeholder="Search by name or ID…" value={search} onChange={e => setSearch(e.target.value)} />
          </div>
          <button className="bi-btn bi-btn-secondary" onClick={exportCSV} title="Download CSV of filtered data">
            Export CSV
          </button>
          <div className="bi-refresh-status">
            <span className={`bi-status-indicator ${loading ? 'updating' : 'ready'}`}></span>
            <span className="bi-status-text">{loading ? 'Updating...' : 'Live'}</span>
          </div>
          <button className="bi-btn bi-btn-primary" disabled={loading} onClick={() => request('/api/plantations').then((d) => Array.isArray(d) && setPlantations(d))}>
            Refresh Data
          </button>
          <button className="bi-btn bi-btn-secondary" onClick={logout}>
            Logout
          </button>
        </div>
      </div>

      {/* Chat Widget */}
      <div className={`chat-widget ${chatOpen ? 'open' : ''}`}>
        <button className="chat-toggle" onClick={() => setChatOpen(!chatOpen)}>
          {chatOpen ? '×' : 'Chat'}
        </button>
        {chatOpen && (
          <div className="chat-container">
            <div className="chat-header">
              <h3>AGX9 Support</h3>
              <span className="chat-status">Online</span>
            </div>
            <div className="chat-messages">
              {chatMessages.map(msg => (
                <div key={msg.id} className={`chat-message ${msg.sender}`}>
                  <div className="chat-bubble">
                    <span className="chat-text">{msg.text}</span>
                    <span className="chat-time">{msg.time}</span>
                  </div>
                </div>
              ))}
            </div>
            <div className="chat-input-area">
              <input
                type="text"
                placeholder="Type your message..."
                value={chatInput}
                onChange={(e) => setChatInput(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && handleSendMessage()}
              />
              <button onClick={handleSendMessage} disabled={!chatInput.trim()}>
                Send
              </button>
            </div>
          </div>
        )}
      </div>

      <div className="bi-kpi-grid">
        <div className="bi-kpi-card">
          <div className="bi-kpi-header">
            <span className="bi-kpi-title">Total Plantations</span>
          </div>
          <div className="bi-kpi-value">{plantations.length}</div>
          <div className="bi-kpi-footer">
            <span className="bi-kpi-trend positive">{filteredPlantations.length === plantations.length ? '—' : `${filteredPlantations.length} shown`}</span>
            <span className="bi-kpi-label">vs last period</span>
          </div>
        </div>

        <div className="bi-kpi-card">
          <div className="bi-kpi-header">
            <span className="bi-kpi-title">Average NDVI</span>
          </div>
          <div className="bi-kpi-value">
            {plantations.length
              ? (plantations.reduce((sum, p) => sum + (Number(p.ndvi) || 0), 0) / plantations.length).toFixed(3)
              : '—'}
          </div>
          <div className="bi-kpi-footer">
            <span className="bi-kpi-trend positive">+2.3%</span>
            <span className="bi-kpi-label">vs last week</span>
          </div>
        </div>

        <div className="bi-kpi-card">
          <div className="bi-kpi-header">
            <span className="bi-kpi-title">Healthy Plantations</span>
          </div>
          <div className="bi-kpi-value">{healthDistribution.healthy + healthDistribution.optimal}</div>
          <div className="bi-kpi-footer">
            <span className="bi-kpi-trend positive">
              {plantations.length ? Math.round(((healthDistribution.healthy + healthDistribution.optimal) / plantations.length) * 100) : 0}%
            </span>
            <span className="bi-kpi-label">health rate</span>
          </div>
        </div>

        <div className="bi-kpi-card">
          <div className="bi-kpi-header">
            <span className="bi-kpi-title">Critical Alerts</span>
          </div>
          <div className="bi-kpi-value">{healthDistribution.unhealthy + healthDistribution.stressed}</div>
          <div className="bi-kpi-footer">
            <span className="bi-kpi-trend negative">
              {plantations.length ? Math.round(((healthDistribution.unhealthy + healthDistribution.stressed) / plantations.length) * 100) : 0}%
            </span>
            <span className="bi-kpi-label">need attention</span>
          </div>
        </div>
      </div>

      <div className="bi-content-grid">
        <div className="bi-map-section">
          <div className="bi-map-toolbar">
            <div className="bi-map-title">
              <h2>Geospatial Map View</h2>
              <span className="bi-map-subtitle">Real-time plantation monitoring</span>
            </div>
            <div className="bi-map-actions">
              <button className="bi-map-action-btn" onClick={() => setTab('legend')}>
                Legend
              </button>
              <button className="bi-map-action-btn" onClick={() => setTab('filters')}>
                Filters
              </button>
            </div>
          </div>
          <div className="bi-map-wrapper">
            <MapView plantations={filteredPlantations} selectedId={selectedId} onSelect={handleSelect} staleThreshold={staleThreshold} />
          </div>
        </div>

        <div className="bi-sidebar">
          <div className="bi-sidebar-section">
            <div className="bi-sidebar-header">
              <h3>Health Distribution</h3>
            </div>
            <div className="bi-chart-container">
              <div className="bi-bar-chart">
                {Object.entries(healthDistribution).map(([key, value]) => (
                  <div key={key} className="bi-bar-item">
                    <div className="bi-bar-label">{key.charAt(0).toUpperCase() + key.slice(1)}</div>
                    <div className="bi-bar-track">
                      <div 
                        className="bi-bar-fill" 
                        style={{ 
                          width: plantations.length ? `${(value / plantations.length) * 100}%` : '0%',
                          background: key === 'unhealthy' ? '#d94e3f' : 
                                     key === 'stressed' ? '#f2a261' : 
                                     key === 'moderate' ? '#e9c46a' : 
                                     key === 'healthy' ? '#8ab17d' : '#2a9d8f'
                        }}
                      ></div>
                    </div>
                    <div className="bi-bar-value">{value}</div>
                  </div>
                ))}
              </div>
            </div>
          </div>

          <div className="bi-sidebar-section">
            <div className="bi-sidebar-header">
              <h3>Top Performers</h3>
            </div>
            <div className="bi-data-table">
              <table>
                <thead>
                  <tr>
                    <th>ID</th>
                    <th>NDVI</th>
                    <th>Status</th>
                  </tr>
                </thead>
                <tbody>
                  {topPerformers.map(p => (
                    <tr key={p.id} className={selectedId === p.id ? 'selected' : ''} onClick={() => setSelectedId(p.id)}>
                      <td>{p.id}</td>
                      <td>{(Number(p.ndvi) || 0).toFixed(3)}</td>
                      <td>
                        <span className={`bi-status-badge ${
                          (Number(p.ndvi) || 0) >= 0.85 ? 'optimal' :
                          (Number(p.ndvi) || 0) >= 0.7 ? 'healthy' :
                          (Number(p.ndvi) || 0) >= 0.5 ? 'moderate' :
                          (Number(p.ndvi) || 0) >= 0.3 ? 'stressed' : 'unhealthy'
                        }`}>
                          {(Number(p.ndvi) || 0) >= 0.85 ? 'Optimal' :
                           (Number(p.ndvi) || 0) >= 0.7 ? 'Healthy' :
                           (Number(p.ndvi) || 0) >= 0.5 ? 'Moderate' :
                           (Number(p.ndvi) || 0) >= 0.3 ? 'Stressed' : 'Unhealthy'}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>

      {selectedPlantation && (
        <div className="bi-detail-panel">
          <div className="bi-detail-header">
            <h3>{selectedPlantation.name || `Plantation #${selectedPlantation.id}`}</h3>
            <button className="bi-modal-close" onClick={() => setSelectedId(null)}>×</button>
          </div>
          <div className="bi-detail-body">
            <div className="bi-detail-row">
              <span className="bi-detail-label">ID</span>
              <span className="bi-detail-value">{selectedPlantation.id}</span>
            </div>
            <div className="bi-detail-row">
              <span className="bi-detail-label">NDVI</span>
              <span className="bi-detail-value">
                <span className={`bi-detail-badge ${healthForNdvi(selectedPlantation.ndvi)}`}>
                  {selectedPlantation.ndvi.toFixed(3)}
                </span>
              </span>
            </div>
            <div className="bi-detail-row">
              <span className="bi-detail-label">Health</span>
              <span className="bi-detail-value">{healthLabel(selectedPlantation.ndvi)}</span>
            </div>
            <div className="bi-detail-row">
              <span className="bi-detail-label">Crop Type</span>
              <span className="bi-detail-value">{selectedPlantation.crop_type || '—'}</span>
            </div>
            <div className="bi-detail-row">
              <span className="bi-detail-label">Area</span>
              <span className="bi-detail-value">{selectedPlantation.area_ha ? `${selectedPlantation.area_ha} ha` : '—'}</span>
            </div>
            <div className="bi-detail-row">
              <span className="bi-detail-label">Last Scan</span>
              <span className="bi-detail-value">
                {selectedPlantation.ndvi_date || '—'}
                {selectedPlantation.isStale && <span className="bi-detail-stale">Stale</span>}
              </span>
            </div>
            {selectedPlantation.notes && (
              <div className="bi-detail-row">
                <span className="bi-detail-label">Notes</span>
                <span className="bi-detail-value">{selectedPlantation.notes}</span>
              </div>
            )}
            <div className="bi-detail-sparkline-section">
              <div className="bi-detail-sparkline-header">NDVI Trend (simulated)</div>
              <div className="bi-detail-sparkline">
                {(() => {
                  const s = sparklinePath(selectedPlantation.ndvi)
                  return (
                    <svg width="100%" height="36" viewBox="0 0 100 36" preserveAspectRatio="none">
                      <path d={s.path} fill="none" stroke={healthColor(selectedPlantation.ndvi)} strokeWidth="2" vectorEffect="non-scaling-stroke" />
                      <circle cx="100" cy={36 - 1 - ((s.latest - s.min) / Math.max(s.max - s.min, 0.05)) * (34)} r="2.5" fill={healthColor(selectedPlantation.ndvi)} />
                    </svg>
                  )
                })()}
              </div>
              <div className="bi-detail-trend-direction">
                {(() => {
                  const s = sparklinePath(selectedPlantation.ndvi)
                  return s.direction === 'up' ? 'Trending upward' : 'Trending downward'
                })()}
              </div>
            </div>
          </div>
        </div>
      )}

      {tab === 'legend' && (
        <div className="bi-modal">
          <div className="bi-modal-content">
            <div className="bi-modal-header">
              <h3>NDVI Health Scale Legend</h3>
              <button className="bi-modal-close" onClick={() => setTab(null)}>×</button>
            </div>
            <div className="bi-modal-body">
              <div className="bi-legend-grid">
                {['Unhealthy', 'Stressed', 'Moderate', 'Healthy', 'Optimal'].map((label, idx) => (
                  <div key={label} className="bi-legend-item">
                    <div className="bi-legend-color" style={{ background: ['#d94e3f', '#f2a261', '#e9c46a', '#8ab17d', '#2a9d8f'][idx] }}></div>
                    <div className="bi-legend-info">
                      <div className="bi-legend-label">{label}</div>
                      <div className="bi-legend-range">
                        {idx === 0 && '< 0.3'}
                        {idx === 1 && '0.3 - 0.5'}
                        {idx === 2 && '0.5 - 0.7'}
                        {idx === 3 && '0.7 - 0.85'}
                        {idx === 4 && '> 0.85'}
                      </div>
                    </div>
                  </div>
                ))}
                <div className="bi-legend-item bi-legend-stale">
                  <div className="bi-legend-color bi-legend-color-stale"></div>
                  <div className="bi-legend-info">
                    <div className="bi-legend-label">Stale Data</div>
                    <div className="bi-legend-range">{'>'} {staleDays} days old</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {tab === 'filters' && (
        <div className="bi-modal">
          <div className="bi-modal-content">
            <div className="bi-modal-header">
              <h3>Data Filters</h3>
              <button className="bi-modal-close" onClick={() => setTab(null)}>×</button>
            </div>
            <div className="bi-modal-body">
              <div className="bi-filter-group">
                <label>Dataset</label>
                <select className="bi-filter-select" value={dataset} onChange={e => setDataset(e.target.value)}>
                  <option value="plantations">Plantations</option>
                  <option value="sensors">Sensors</option>
                  <option value="trucks">Fleet</option>
                  <option value="carbon">Carbon Credits</option>
                </select>
              </div>
              <div className="bi-filter-group">
                <label>NDVI Range</label>
                <div className="bi-filter-range">
                  <input type="number" placeholder="Min" step="0.1" min="0" max="1" value={ndviMin} onChange={e => setNdviMin(e.target.value)} />
                  <span>to</span>
                  <input type="number" placeholder="Max" step="0.1" min="0" max="1" value={ndviMax} onChange={e => setNdviMax(e.target.value)} />
                </div>
              </div>
              <div className="bi-filter-group">
                <label>Health Status</label>
                <div className="bi-filter-checkboxes">
                  {[['unhealthy', 'Unhealthy'], ['stressed', 'Stressed'], ['moderate', 'Moderate'], ['healthy', 'Healthy'], ['optimal', 'Optimal']].map(([key, label]) => (
                    <label key={key} className="bi-checkbox">
                      <input type="checkbox" checked={healthFilters[key]} onChange={() => setHealthFilters(f => ({ ...f, [key]: !f[key] }))} />
                      <span>{label}</span>
                    </label>
                  ))}
                </div>
              </div>
              <div className="bi-filter-group">
                <label>Area (ha)</label>
                <div className="bi-filter-range">
                  <input type="number" placeholder="Min ha" step="0.1" min="0" value={areaMin} onChange={e => setAreaMin(e.target.value)} />
                  <span>to</span>
                  <input type="number" placeholder="Max ha" step="0.1" min="0" value={areaMax} onChange={e => setAreaMax(e.target.value)} />
                </div>
              </div>
              {cropTypes.length > 0 && (
                <div className="bi-filter-group">
                  <label>Crop Type</label>
                  <select className="bi-filter-select" value={cropType} onChange={e => setCropType(e.target.value)}>
                    <option value="">All crops</option>
                    {cropTypes.map(t => <option key={t} value={t}>{t}</option>)}
                  </select>
                </div>
              )}
              <div className="bi-filter-actions">
                <button className="bi-btn bi-btn-secondary" onClick={() => {
                  setNdviMin(''); setNdviMax('')
                  setAreaMin(''); setAreaMax('')
                  setHealthFilters({ unhealthy: true, stressed: true, moderate: true, healthy: true, optimal: true })
                  setCropType('')
                  setSearch('')
                }}>Reset</button>
                <button className="bi-btn bi-btn-primary" onClick={() => setTab(null)}>Apply Filters</button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default Dashboard
